import { Input } from './ui/input';
import { useOperatorLocale } from '../hooks/useOperatorLocale';

/**
 * MoneyInput — a currency-aware amount field. The currency symbol ("$", "AED",
 * "£") and its position (prefix/suffix) come from the operator's locale
 * (`useOperatorLocale`), so no component ever hard-codes a currency again.
 *
 * Keeps the existing numeric data contract: emits a Number (or '' when empty
 * and `allowEmpty`). Editing surfaces use this everywhere a price is entered.
 *
 * Usage:
 *   <MoneyInput value={opt.price} onChange={(v) => updateDuration(i, 'price', v)} />
 */
export const MoneyInput = ({
  value,
  onChange,
  placeholder = '0.00',
  className = '',
  disabled = false,
  allowEmpty = false,
  min = 0,
  inputClassName = '',
  ...props
}) => {
  const { currencySymbol, currencyPosition } = useOperatorLocale();
  const isSuffix = currencyPosition === 'suffix';
  const multiChar = (currencySymbol || '').length > 1; // e.g. "AED"

  const padClass = isSuffix
    ? (multiChar ? 'pr-16' : 'pr-10')
    : (multiChar ? 'pl-14' : 'pl-8');

  const displayValue = value === '' || value === null || value === undefined
    ? ''
    : value;

  const handleChange = (e) => {
    const raw = e.target.value;
    if (raw === '') {
      onChange(allowEmpty ? '' : 0);
      return;
    }
    const num = parseFloat(raw);
    onChange(Number.isFinite(num) ? num : (allowEmpty ? '' : 0));
  };

  return (
    <div className={`relative ${className}`}>
      {!isSuffix && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium pointer-events-none text-sm">
          {currencySymbol}
        </span>
      )}
      <Input
        type="number"
        step="0.01"
        min={min}
        inputMode="decimal"
        value={displayValue}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        className={`${padClass} ${inputClassName}`}
        {...props}
      />
      {isSuffix && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium pointer-events-none text-sm">
          {currencySymbol}
        </span>
      )}
    </div>
  );
};

export default MoneyInput;
